// src/components/tables/ClientActiveProjectsTable.jsx
import React from 'react';
import ProjectNameLink from '../common/ProjectNameLink';
import PropTypes from 'prop-types';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow,
  Button,
  useTheme,
  Chip,
  CircularProgress
} from '@mui/material';

/**
 * Format date to short mm/dd/yy format
 * @param {string} dateString - Date string to format
 * @returns {string} Formatted date string
 */
const formatShortDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit'
  });
};

/**
 * Get status color for project status
 * @param {string} status - Project status
 * @returns {string} Color code
 */
const getStatusColor = (status) => {
  if (!status) return '#757575';
  const statusLower = status.toLowerCase();
  
  if (statusLower === 'ongoing' || statusLower === 'active' || statusLower === 'in progress') {
    return '#03DAC6'; // Green for ongoing
  }
  if (statusLower === 'planned' || statusLower === 'upcoming') {
    return '#64B5F6'; // Blue for planned
  }
  return '#FFCF44'; // Yellow for other statuses
};

/**
 * Filter projects to show only active/planned ones
 * @param {Array} projects - All projects
 * @returns {Array} Filtered active projects
 */
const filterActiveProjects = (projects) => {
  if (!Array.isArray(projects)) return [];
  
  return projects.filter(project => {
    const status = project.status?.toLowerCase();
    return status === 'ongoing' || 
           status === 'planned' || 
           status === 'active' ||
           status === 'in progress' ||
           status === 'upcoming';
  });
};

/**
 * ClientActiveProjectsTable component displays active/planned client projects
 * Similar to EngagementsTable but for client projects
 */
const ClientActiveProjectsTable = ({ 
  projects = [], 
  clientId,
  limit = 3,
  showMore = false,
  onShowMoreClick,
  isLoading = false
}) => {
  const theme = useTheme();
  
  // Background color from theme or fallback
  const bgColor = theme.palette.background.paper || '#1A1A1A';
  
  // Filter to show only active/planned projects
  const activeProjects = filterActiveProjects(projects);
  
  // Display projects up to the limit
  const displayProjects = activeProjects.slice(0, limit);

  if (isLoading) {
    return (
      <Box sx={{ 
        px: 1.5, 
        pt: 1.5, 
        pb: 1.5,
        backgroundColor: bgColor,
        borderRadius: 1
      }}>
        <Typography 
          sx={{ 
            fontSize: '0.8rem', 
            fontWeight: 600, 
            mb: 0.5,
            textTransform: 'uppercase',
            color: theme.palette.text.primary
          }}
        >
          Active Projects
        </Typography>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <CircularProgress size={20} sx={{ color: 'primary.main' }} />
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}>
            Loading projects...
          </Typography>
        </Box>
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      px: 1.5, 
      pt: 1.5, 
      pb: showMore ? 1 : 1.5,
      backgroundColor: bgColor,
      borderRadius: 1
    }}>
      <Typography 
        sx={{ 
          fontSize: '0.8rem', 
          fontWeight: 600, 
          mb: 0.5,
          textTransform: 'uppercase',
          color: theme.palette.text.primary
        }}
      >
        Active Projects
      </Typography>

      <Table 
        size="small" 
        sx={{ 
          mb: 0.5,
          '& .MuiTableCell-root': {
            py: 0.5,
            px: 1,
            borderColor: 'rgba(255, 255, 255, 0.1)',
            fontSize: '0.7rem'
          },
          '& .MuiTableCell-head': {
            fontWeight: 'bold',
            color: theme.palette.text.secondary,
            fontSize: '0.65rem',
            textTransform: 'uppercase',
            pb: 0.25
          }
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Project</TableCell>
            <TableCell>Start</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayProjects && displayProjects.length > 0 ? (
            displayProjects.map((project, index) => (
              <TableRow 
                key={project.project_id || index}
                sx={{
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(187, 134, 252, 0.08)',
                    cursor: 'pointer'
                  }
                }}
              >
                <TableCell sx={{ color: 'white', fontWeight: 500 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '120px'
                    }}
                    title={project.project_name || project.name || 'Unknown Project'}
                  >
                    <ProjectNameLink project={project}>
                      {project.project_name || project.name || 'Unknown Project'}
                    </ProjectNameLink>
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box component="span" sx={{
                    color: 'white',
                    fontWeight: 'medium'
                  }}>
                    {formatShortDate(project.start_date)}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={project.status || 'Unknown'} 
                    size="small"
                    sx={{ 
                      fontSize: '0.6rem',
                      height: 18,
                      bgcolor: `${getStatusColor(project.status)}30`,
                      color: getStatusColor(project.status),
                      fontWeight: 'medium'
                    }}
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={3}
                align="center"
                sx={{ 
                  py: 2,
                  color: theme.palette.text.secondary,
                  fontStyle: 'italic'
                }}
              >
                No active projects
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      
      {/* "Show more" button */}
      {showMore && onShowMoreClick && (
        <Button 
          variant="text" 
          size="small" 
          onClick={(e) => {
            e.stopPropagation(); // Prevent card click event
            onShowMoreClick();  // Open the expanded view
          }}
          sx={{ 
            fontSize: '0.65rem', 
            color: theme.palette.primary.main,
            display: 'block',
            margin: '0 auto',
            padding: '2px 8px',
            '&:hover': {
              backgroundColor: 'rgba(187, 134, 252, 0.08)'
            }
          }}
        >
          SHOW MORE
        </Button>
      )}
    </Box>
  );
};

ClientActiveProjectsTable.propTypes = {
  projects: PropTypes.array,
  clientId: PropTypes.string.isRequired,
  limit: PropTypes.number,
  showMore: PropTypes.bool,
  onShowMoreClick: PropTypes.func,
  isLoading: PropTypes.bool
};

export default ClientActiveProjectsTable;
